import { Button, QuantityControl } from '@/components/ui'
import { memo } from 'react'
import { formatPrice } from '@/utils/formatting'
import type { CartItem as CartItemType } from '@/types/cart'

interface CartItemProps {
  item: CartItemType
  onQuantityChange: (itemId: string, quantity: number) => void
  onRemove: (itemId: string) => void
}

export const CartItem = memo(function CartItem({ item, onQuantityChange, onRemove }: CartItemProps) {
  return (
    <div className="flex items-start gap-3 min-w-0 w-full">
      {/* Image */}
      <div className="h-[72px] w-[72px] rounded-xl overflow-hidden bg-muted flex-shrink-0">
        {item.menuItem.image_url ? (
          <img
            src={item.menuItem.image_url}
            alt={item.menuItem.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-muted" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[15px] leading-snug truncate">{item.menuItem.name}</div>

        {/* Show selected ingredients for single meals */}
        {item.selectedModifiers && item.selectedModifiers.length > 0 && (
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {item.selectedModifiers.map((mod, idx) => (
              <span key={`${mod.groupId}-${mod.optionId}`}>
                {mod.optionName}
                {mod.variationName && ` (${mod.variationName})`}
                {idx < item.selectedModifiers.length - 1 && ', '}
              </span>
            ))}
            {item.comboSelections?.ingredientVariationSelections &&
              item.comboSelections.ingredientVariationSelections.length > 0 && (
                <>
                  {' · '}
                  {item.comboSelections.ingredientVariationSelections.map((vs, idx) => (
                    <span key={`${vs.rowIngredientId}-${vs.variationId}`}>
                      {vs.ingredientName}: {vs.variationName}
                      {idx < (item.comboSelections?.ingredientVariationSelections?.length ?? 0) - 1 && ' · '}
                    </span>
                  ))}
                </>
              )}
          </div>
        )}

        {/* Show combo customizations and selections */}
        {item.comboSelections && (
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {item.comboSelections.mainMealCustomizations && item.comboSelections.mainMealCustomizations.length > 0 && (
              <span>
                {item.comboSelections.mainMealCustomizations.map((mod, idx) => (
                  <span key={`${mod.groupId}-${mod.optionId}`}>
                    {mod.optionName}
                    {mod.variationName && ` (${mod.variationName})`}
                    {idx < item.comboSelections!.mainMealCustomizations!.length - 1 && ', '}
                  </span>
                ))}
              </span>
            )}
            {item.comboSelections.groupSelections.length > 0 && (
              <span>
                {item.comboSelections.mainMealCustomizations?.length ? ' · ' : ''}
                {item.comboSelections.groupSelections.map((sel, idx) => (
                  <span key={sel.groupId}>
                    {sel.optionName}
                    {idx < item.comboSelections!.groupSelections.length - 1 && ', '}
                  </span>
                ))}
              </span>
            )}
          </div>
        )}

        {/* Fallback to description if no customizations */}
        {!item.selectedModifiers?.length && !item.comboSelections && item.menuItem.description && (
          <div className="text-xs text-muted-foreground mt-0.5 truncate">
            {item.menuItem.description}
          </div>
        )}

        {/* Price */}
        <div className="text-sm font-semibold mt-1.5">
          {formatPrice(item.itemTotal / Math.max(1, item.quantity), item.menuItem.currency)}
        </div>

        {/* Quantity Controls — inline below text */}
        <div className="flex items-center gap-3 mt-2">
          <QuantityControl
            value={item.quantity}
            onDecrement={() => onQuantityChange(item.id, item.quantity - 1)}
            onIncrement={() => onQuantityChange(item.id, item.quantity + 1)}
            size="sm"
            variant="ghost"
          />
          <Button
            variant="link"
            className="h-auto p-0 text-xs text-muted-foreground hover:text-destructive"
            onClick={() => onRemove(item.id)}
            aria-label={`Remove ${item.menuItem.name} from cart`}
          >
            Remove
          </Button>
        </div>
      </div>
    </div>
  )
})
